import React, { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Horizontal snap-scroll carousel with visible arrow controls.
 * Designed to signal "there's more content — swipe or tap the arrows".
 */
const HorizontalCarousel = ({
  children,
  testId = "h-carousel",
  itemMinWidth = "min-w-[240px] sm:min-w-[280px]",
  arrowsAlways = true,
  autoScrollMs = 0,
}) => {
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);
  const [paused, setPaused] = useState(false);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 5);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, []);

  // Auto-scroll behaviour — pauses on hover/touch, wraps to start when reaching end
  useEffect(() => {
    if (!autoScrollMs) return;
    const el = scrollerRef.current;
    if (!el) return;
    const id = setInterval(() => {
      if (paused || document.hidden) return;
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 5) {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        el.scrollBy({ left: el.clientWidth * 0.85, behavior: "smooth" });
      }
    }, autoScrollMs);
    return () => clearInterval(id);
  }, [autoScrollMs, paused]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: "smooth" });
  };

  const pause = () => setPaused(true);
  const resume = () => setPaused(false);

  return (
    <div
      data-testid={testId}
      className="relative"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onTouchStart={pause}
      onTouchEnd={resume}
    >
      {(arrowsAlways || canLeft) && (
        <button
          data-testid={`${testId}-prev`}
          aria-label="Scroll left"
          onClick={() => scrollBy(-1)}
          disabled={!canLeft}
          className={`hidden sm:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white/95 border border-[#F39C12]/40 items-center justify-center shadow-md transition ${
            canLeft ? "hover:bg-white opacity-100" : "opacity-40 cursor-not-allowed"
          }`}
        >
          <ChevronLeft className="w-5 h-5 text-[#2C3E50]" />
        </button>
      )}

      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-thin"
        style={{ scrollbarColor: "#F39C12 transparent" }}
      >
        {React.Children.map(children, (child, i) => (
          <div
            key={i}
            className={`shrink-0 snap-center ${itemMinWidth}`}
          >
            {child}
          </div>
        ))}
      </div>

      {(arrowsAlways || canRight) && (
        <button
          data-testid={`${testId}-next`}
          aria-label="Scroll right"
          onClick={() => scrollBy(1)}
          disabled={!canRight}
          className={`hidden sm:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white/95 border border-[#F39C12]/40 items-center justify-center shadow-md transition ${
            canRight ? "hover:bg-white opacity-100" : "opacity-40 cursor-not-allowed"
          }`}
        >
          <ChevronRight className="w-5 h-5 text-[#2C3E50]" />
        </button>
      )}

      {/* Mobile swipe hint */}
      <div className="sm:hidden mt-2 text-center text-xs text-[#7F8C8D]">
        ← Swipe for more →
      </div>
    </div>
  );
};

export default HorizontalCarousel;
